import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { IconTicket as Ticket, IconPlus as Plus, IconPencil as Pencil, IconTrash as Trash2, IconStar as Star, IconCheck as Check, IconX as X, IconClockFast as Clock, IconInfo as Info } from "@/components/BrandGraphics";

const LICENSE_TYPES = ["Permis B", "Permis A1", "Permis A2", "Permis A", "Permis AM", "Permis BE", "Permis C", "Permis D"];
const GEARBOXES = ["Boîte manuelle", "Boîte automatique", "Les deux au même prix"];

const EMPTY = {
  id: null, title: "", price: "", hours: "", options_text: "",
  license_type: "Permis B", gearbox: "", code_included: null, city: "", postal_code: "", active: true, featured: false,
};

function StatusBadge({ a }) {
  if (a.suspended) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700"><X className="h-3.5 w-3.5" /> Suspendue par Plan Permis</span>;
  }
  if (a.active === false) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-500"><Clock className="h-3.5 w-3.5" /> Désactivée (non visible)</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" /> En ligne</span>;
}

export default function ProAffiches() {
  const [affiches, setAffiches] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/partner/affiches"),
        api.get("/school/profile").catch(() => ({ data: null })),
      ]);
      setAffiches(a.data || []);
      setProfile(p.data || null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => {
    setForm({
      ...EMPTY,
      city: profile?.city || "",
      postal_code: profile?.postal_code || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const edit = (a) => {
    setForm({
      id: a.id, title: a.title || "", price: a.price ?? "", hours: a.hours ?? "",
      options_text: (a.options || []).join("\n"),
      license_type: a.license_type || "Permis B", gearbox: a.gearbox || "",
      code_included: typeof a.code_included === "boolean" ? a.code_included : null,
      city: a.city || "", postal_code: a.postal_code || "", active: a.active !== false,
      featured: !!a.featured,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => { setForm(EMPTY); setShowForm(false); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre de la formule est obligatoire."); return; }
    if (!/^\d{5}$/.test(form.postal_code)) { toast.error("Code postal à 5 chiffres."); return; }
    const payload = {
      title: form.title.trim(),
      price: Number(form.price) || 0,
      hours: parseInt(form.hours, 10) || 0,
      options: form.options_text.split("\n").map((s) => s.trim()).filter(Boolean),
      license_type: form.license_type,
      gearbox: form.gearbox,
      code_included: form.code_included,
      city: form.city.trim(),
      postal_code: form.postal_code.trim(),
      active: form.active,
      featured: form.featured,
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/partner/affiches/${form.id}`, payload);
        toast.success("Annonce mise à jour et publiée.");
      } else {
        await api.post("/partner/affiches", payload);
        toast.success("Annonce publiée en ligne.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'annonce « ${a.title} » ?`)) return;
    try {
      await api.delete(`/partner/affiches/${a.id}`);
      toast.success("Annonce supprimée.");
      if (form.id === a.id) cancel();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const makeFeatured = async (a) => {
    try {
      await api.post(`/partner/affiches/${a.id}/featured`);
      toast.success("Offre mise en avant.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const inputCls = "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-brand";

  return (
    <Shell role="school">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Ticket className="h-6 w-6 text-brand" />
            <h1 className="text-3xl font-extrabold tracking-tighter">Mes annonces</h1>
          </div>
          {!showForm && (
            <button onClick={startNew} data-testid="pro-affiche-new-btn"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
              <Plus className="h-4 w-4" /> Nouvelle annonce
            </button>
          )}
        </div>
        <p className="mt-2 max-w-2xl text-neutral-500">
          Publiez uniquement vos <strong>forfaits en permis accéléré</strong> et les places que vous
          réservez chaque mois. Une seule offre est « mise en avant » (vue en premier par les futurs élèves).
          Vos annonces sont <strong>anonymes</strong> côté public et <strong>mises en ligne automatiquement</strong>.
          Plan Permis peut suspendre a posteriori toute annonce non conforme (vous êtes alors notifié avec le motif).
        </p>

        {showForm && (
          <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="pro-affiche-form">
            <h2 className="text-lg font-bold">{form.id ? "Modifier l'annonce" : "Nouvelle annonce"}</h2>
            <div className="mt-2 flex items-start gap-2 rounded-lg bg-brand/5 px-3 py-2 text-xs text-brand">
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Formule <strong>permis accéléré</strong> uniquement. Précisez si le <strong>code</strong> est inclus et le type de <strong>boîte</strong> (manuelle, automatique, ou les deux au même prix). Ajoutez librement vos autres options.</span>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Titre de la formule (accéléré)</label>
                <input value={form.title} onChange={(e) => upd("title", e.target.value)}
                  data-testid="pro-affiche-title-input" placeholder="Ex : Permis B accéléré — 20 h" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Prix (€)</label>
                <input value={form.price} onChange={(e) => upd("price", e.target.value)} type="number" min="0"
                  data-testid="pro-affiche-price-input" placeholder="1290" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Nombre d'heures</label>
                <input value={form.hours} onChange={(e) => upd("hours", e.target.value)} type="number" min="0"
                  data-testid="pro-affiche-hours-input" placeholder="20" className={inputCls} />
                <span className="mt-1 block text-[11px] text-amber-700">{"Entre 10 et 20 h : badge « Idéal pour repasser » automatique (élèves qui ont raté l'examen)."}</span>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Type de permis</label>
                <select value={form.license_type} onChange={(e) => upd("license_type", e.target.value)}
                  data-testid="pro-affiche-license-select" className={inputCls}>
                  {LICENSE_TYPES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Boîte</label>
                <select value={form.gearbox} onChange={(e) => upd("gearbox", e.target.value)}
                  data-testid="pro-affiche-gearbox-select" className={inputCls}>
                  <option value="">Peu importe</option>
                  {GEARBOXES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Code de la route inclus ?</label>
                <select
                  value={form.code_included === true ? "oui" : form.code_included === false ? "non" : ""}
                  onChange={(e) => upd("code_included", e.target.value === "oui" ? true : e.target.value === "non" ? false : null)}
                  data-testid="pro-affiche-code-select" className={inputCls}>
                  <option value="">À préciser</option>
                  <option value="oui">Oui, code inclus</option>
                  <option value="non">Non, code non inclus</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Ville</label>
                <input value={form.city} onChange={(e) => upd("city", e.target.value)}
                  data-testid="pro-affiche-city-input" placeholder="Lyon" className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Code postal</label>
                <input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)}
                  data-testid="pro-affiche-postal-input" placeholder="69003" maxLength={5} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500">Options (une par ligne)</label>
                <textarea value={form.options_text} onChange={(e) => upd("options_text", e.target.value)}
                  data-testid="pro-affiche-options-input" rows={4}
                  placeholder={"Code inclus\nExamen garanti sous 30 jours\nSuivi personnalisé"} className={inputCls} />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)}
                    data-testid="pro-affiche-active-toggle" className="h-4 w-4 accent-brand" />
                  Annonce active (visible une fois validée)
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <input type="checkbox" checked={form.featured} onChange={(e) => upd("featured", e.target.checked)}
                    data-testid="pro-affiche-featured-toggle" className="h-4 w-4 accent-brand" />
                  Mettre cette offre en avant (affichée en premier aux futurs élèves)
                </label>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="pro-affiche-save-btn"
                className="inline-flex items-center gap-2 rounded-md bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
                <Plus className="h-4 w-4" /> {saving ? "Enregistrement…" : (form.id ? "Mettre à jour" : "Publier l'annonce")}
              </button>
              <button onClick={cancel} data-testid="pro-affiche-cancel-btn"
                className="rounded-md border border-neutral-300 px-6 py-2.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50">
                Annuler
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4" data-testid="pro-affiches-list">
          {affiches.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-white p-10 text-center text-neutral-400">
              Aucune annonce pour l'instant. Créez votre première offre.
            </div>
          ) : affiches.map((a) => (
            <div key={a.id} data-testid={`pro-affiche-row-${a.id}`}
              className={`rounded-2xl border bg-white p-5 shadow-sm ${a.featured ? "border-brand/40" : "border-neutral-200"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-neutral-900">{a.title}</h3>
                    {a.featured && (
                      <span data-testid={`pro-affiche-featured-badge-${a.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star className="h-3 w-3" /> Mise en avant</span>
                    )}
                    <StatusBadge a={a} />
                  </div>
                  <p className="mt-1 text-sm text-neutral-500">
                    {Math.round(a.price)} € · {a.hours} h · {a.license_type}{a.gearbox ? ` · ${a.gearbox}` : ""} · {a.city} ({a.postal_code})
                  </p>
                  <p className="mt-1 text-xs font-semibold text-neutral-400">
                    {a.code_included === true ? "Code inclus" : a.code_included === false ? "Code non inclus" : "Code : à préciser"}
                  </p>
                  {a.options?.length > 0 && (
                    <p className="mt-1 text-xs text-neutral-400">{a.options.join(" · ")}</p>
                  )}
                  {a.suspended && a.suspend_reason && (
                    <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
                      data-testid={`pro-affiche-note-${a.id}`}>
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Suspendue par Plan Permis — motif : {a.suspend_reason}. Corrigez votre annonce puis enregistrez-la : elle sera remise en ligne automatiquement.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  {!a.featured && !a.suspended && a.active !== false && (
                    <button onClick={() => makeFeatured(a)} data-testid={`pro-affiche-feature-${a.id}`}
                      title="Mettre en avant" className="rounded-md border border-neutral-300 p-2 text-neutral-500 hover:bg-neutral-100 hover:text-brand"><Star className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => edit(a)} data-testid={`pro-affiche-edit-${a.id}`}
                    className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(a)} data-testid={`pro-affiche-delete-${a.id}`}
                    className="rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
